import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building, CheckCircle2, ChevronDown, Filter, Folder, Loader2, Search, Users, X, XCircle } from 'lucide-react';
import { Layout } from '../components/Layout';
import { IdeasFilterTable } from '../components/IdeasFilterTable';
import { MisDecisionModal } from '../components/MisDecisionModal';
import { apiClient } from '../api/client';

type Person = {
  id: string;
  name: string;
  role: string;
  managedDepartments?: string[];
  reportsToId?: string | null;
};

function toIdeaDepartments(groups: { name: string; departments: string[] }[], names: string[]) {
  const out = new Set<string>();
  names.forEach((name) => {
    const group = groups.find((item) => item.name === name);
    if (group?.departments?.length) {
      group.departments.forEach((department) => out.add(department));
    } else {
      out.add(name);
    }
  });
  return Array.from(out);
}

export function MisPortalPage() {
  const [selectedCommitteeNames, setSelectedCommitteeNames] = useState<string[]>([]);
  const [selectedLeaderIds, setSelectedLeaderIds] = useState<string[]>([]);
  const [selectedHeadIds, setSelectedHeadIds] = useState<string[]>([]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [committeeSearch, setCommitteeSearch] = useState('');
  const [leaderSearch, setLeaderSearch] = useState('');
  const [headSearch, setHeadSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [openMenu, setOpenMenu] = useState<'committee' | 'leader' | 'head' | 'member' | null>(null);
  const [decisionIdeaId, setDecisionIdeaId] = useState<string | null>(null);

  const committeeRef = useRef<HTMLDivElement>(null);
  const leaderRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const memberRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (committeeRef.current && !committeeRef.current.contains(target) && openMenu === 'committee') setOpenMenu(null);
      if (leaderRef.current && !leaderRef.current.contains(target) && openMenu === 'leader') setOpenMenu(null);
      if (headRef.current && !headRef.current.contains(target) && openMenu === 'head') setOpenMenu(null);
      if (memberRef.current && !memberRef.current.contains(target) && openMenu === 'member') setOpenMenu(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenu]);

  const { data: departmentGroups = [] } = useQuery({
    queryKey: ['misDepartmentGroups'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/mis/department-groups');
      return (res.data.data || []) as { id: string; name: string; departments: string[] }[];
    },
  });

  const { data: userRoleMappings = [] } = useQuery({
    queryKey: ['misUserRoleMappings'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/mis/user-role-mappings');
      return (res.data.data || []) as Person[];
    },
  });

  const allCommittees = Array.from(new Set(departmentGroups.map((group) => group.name))).sort();
  const filteredCommittees = allCommittees.filter((name) => name.toLowerCase().includes(committeeSearch.toLowerCase()));
  const allLeaders = userRoleMappings.filter((user) => user.role === 'LEADER');
  const filteredLeaders = selectedCommitteeNames.length > 0
    ? allLeaders.filter((leader) => leader.managedDepartments?.some((department) => selectedCommitteeNames.includes(department)))
    : allLeaders;
  const searchedLeaders = filteredLeaders.filter((leader) => leader.name.toLowerCase().includes(leaderSearch.toLowerCase()));
  const selectedLeaders = filteredLeaders.filter((leader) => selectedLeaderIds.includes(leader.id));

  let activeDepartmentsForHeads: string[] = selectedLeaders.length > 0
    ? Array.from(new Set(selectedLeaders.flatMap((leader) => leader.managedDepartments || [])))
    : [...selectedCommitteeNames];
  if (selectedCommitteeNames.length > 0 && selectedLeaders.length > 0) {
    activeDepartmentsForHeads = activeDepartmentsForHeads.filter((department) => selectedCommitteeNames.includes(department));
  }
  const hasActiveScope = activeDepartmentsForHeads.length > 0;

  const committeeHeads = userRoleMappings.filter((user) =>
    user.role === 'COMMITTEE_HEAD' &&
    (!hasActiveScope || user.managedDepartments?.some((department) => activeDepartmentsForHeads.includes(department)))
  );
  const searchedHeads = committeeHeads.filter((head) => head.name.toLowerCase().includes(headSearch.toLowerCase()));
  const selectedHeadsList = userRoleMappings.filter((user) => user.role === 'COMMITTEE_HEAD' && selectedHeadIds.includes(user.id));
  const allCommitteeMembers = userRoleMappings.filter((user) =>
    user.role === 'COMMITTEE_MEMBER' &&
    (!hasActiveScope || user.managedDepartments?.some((department) => activeDepartmentsForHeads.includes(department)))
  );
  const filteredMembers = selectedHeadIds.length > 0
    ? (() => {
      const headMappedDepts = new Set(selectedHeadsList.flatMap((head) => head.managedDepartments || []));
      const pool = hasActiveScope
        ? allCommitteeMembers
        : userRoleMappings.filter((user) => user.role === 'COMMITTEE_MEMBER');
      return pool.filter((member) => (member.managedDepartments || []).some((department) => headMappedDepts.has(department)));
    })()
    : allCommitteeMembers;
  const searchedMembers = filteredMembers.filter((member) => member.name.toLowerCase().includes(memberSearch.toLowerCase()));

  let effectiveDepartmentIds: string | undefined;
  let finalAssigneeIds: string[] = [];
  if (selectedAssigneeIds.length > 0) {
    finalAssigneeIds = selectedAssigneeIds;
  } else if (selectedHeadIds.length > 0) {
    let headDepts = Array.from(new Set(selectedHeadsList.flatMap((head) => head.managedDepartments || [])));
    if (selectedCommitteeNames.length > 0) headDepts = headDepts.filter((department) => selectedCommitteeNames.includes(department));
    if (headDepts.length > 0) effectiveDepartmentIds = toIdeaDepartments(departmentGroups, headDepts).join('|');
    else finalAssigneeIds = filteredMembers.map((member) => member.id);
  } else if (selectedLeaderIds.length > 0) {
    let leaderDepts = Array.from(new Set(
      userRoleMappings
        .filter((user) => user.role === 'LEADER' && selectedLeaderIds.includes(user.id))
        .flatMap((leader) => leader.managedDepartments || [])
    ));
    if (selectedCommitteeNames.length > 0) leaderDepts = leaderDepts.filter((department) => selectedCommitteeNames.includes(department));
    if (leaderDepts.length > 0) effectiveDepartmentIds = toIdeaDepartments(departmentGroups, leaderDepts).join('|');
    else finalAssigneeIds = filteredMembers.map((member) => member.id);
  } else if (selectedCommitteeNames.length > 0) {
    effectiveDepartmentIds = toIdeaDepartments(departmentGroups, selectedCommitteeNames).join('|');
  }

  const { data: globalStats, isLoading: statsLoading } = useQuery({
    queryKey: ['misStats', finalAssigneeIds.join(','), effectiveDepartmentIds],
    queryFn: async () => {
      const res = await apiClient.get('/admin/mis/stats', {
        params: {
          assigneeId: finalAssigneeIds.length > 0 ? finalAssigneeIds.join(',') : undefined,
          department: effectiveDepartmentIds || undefined,
        },
      });
      return res.data.data;
    },
  });

  const cards = [
    { label: 'Total Ideas', value: globalStats?.kpis?.totalIdeas || 0, icon: <Folder className="w-3.5 h-3.5" />, tone: 'bg-blue-50 text-blue-600' },
    { label: 'MIS Approved', value: globalStats?.kpis?.misApproved || 0, icon: <CheckCircle2 className="w-3.5 h-3.5" />, tone: 'bg-emerald-50 text-emerald-600' },
    { label: 'Reject (Duplicate)', value: globalStats?.kpis?.misRejected || 0, icon: <XCircle className="w-3.5 h-3.5" />, tone: 'bg-rose-50 text-rose-600' },
  ];

  const hasTopFilters = selectedCommitteeNames.length > 0 || selectedLeaderIds.length > 0 || selectedHeadIds.length > 0 || selectedAssigneeIds.length > 0;

  return (
    <Layout title="MIS Portal">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-slate-800">MIS Portal</h2>
        <p className="text-slate-500 text-xs mt-0.5">Review, prioritize and moderate idea workflow</p>
      </div>

      <div className="bg-white p-3 sm:p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row flex-wrap gap-3 sm:gap-4 mt-2 mb-2">
        <FilterMenu
          menuRef={committeeRef}
          open={openMenu === 'committee'}
          onToggle={() => setOpenMenu(openMenu === 'committee' ? null : 'committee')}
          icon={<Building className="w-3 h-3 text-purple-500" />}
          label="Committee"
          value={selectedCommitteeNames.length === 0 ? 'All Committees' : selectedCommitteeNames.length === 1 ? selectedCommitteeNames[0] : `${selectedCommitteeNames.length} Selected`}
          search={committeeSearch}
          onSearch={setCommitteeSearch}
          searchPlaceholder="Search committees..."
          accent="purple"
          allChecked={selectedCommitteeNames.length === allCommittees.length && allCommittees.length > 0}
          onToggleAll={(checked) => {
            setSelectedCommitteeNames(checked ? allCommittees : []);
            setSelectedLeaderIds([]);
            setSelectedHeadIds([]);
            setSelectedAssigneeIds([]);
          }}
          emptyText="No committees found"
          options={filteredCommittees.map((name) => ({
            id: name,
            title: name,
            checked: selectedCommitteeNames.includes(name),
            onChange: (checked: boolean) => {
              setSelectedCommitteeNames((current) => checked ? [...current, name] : current.filter((item) => item !== name));
              setSelectedLeaderIds([]);
              setSelectedHeadIds([]);
              setSelectedAssigneeIds([]);
            },
          }))}
        />
        <FilterMenu
          menuRef={leaderRef}
          open={openMenu === 'leader'}
          onToggle={() => setOpenMenu(openMenu === 'leader' ? null : 'leader')}
          icon={<Users className="w-3 h-3 text-blue-500" />}
          label="Committee Leader"
          value={selectedLeaderIds.length === 0 ? 'All Leaders' : selectedLeaderIds.length === 1 ? (filteredLeaders.find((leader) => leader.id === selectedLeaderIds[0])?.name || 'Selected') : `${selectedLeaderIds.length} Selected`}
          search={leaderSearch}
          onSearch={setLeaderSearch}
          searchPlaceholder="Search leaders..."
          accent="blue"
          allChecked={selectedLeaderIds.length === filteredLeaders.length && filteredLeaders.length > 0}
          onToggleAll={(checked) => {
            setSelectedLeaderIds(checked ? filteredLeaders.map((leader) => leader.id) : []);
            setSelectedHeadIds([]);
            setSelectedAssigneeIds([]);
          }}
          emptyText="No leaders found"
          options={searchedLeaders.map((leader) => ({
            id: leader.id,
            title: leader.name,
            subtitle: (selectedCommitteeNames.length > 0
              ? (leader.managedDepartments || []).filter((department) => selectedCommitteeNames.includes(department))
              : (leader.managedDepartments || [])
            ).join(' | '),
            checked: selectedLeaderIds.includes(leader.id),
            onChange: (checked: boolean) => {
              setSelectedLeaderIds((current) => checked ? [...current, leader.id] : current.filter((id) => id !== leader.id));
              setSelectedHeadIds([]);
              setSelectedAssigneeIds([]);
            },
          }))}
        />
        <FilterMenu
          menuRef={headRef}
          open={openMenu === 'head'}
          onToggle={() => setOpenMenu(openMenu === 'head' ? null : 'head')}
          icon={<Users className="w-3 h-3 text-emerald-500" />}
          label="Committee Head"
          value={selectedHeadIds.length === 0 ? 'All Heads' : selectedHeadIds.length === 1 ? (committeeHeads.find((head) => head.id === selectedHeadIds[0])?.name || 'Selected') : `${selectedHeadIds.length} Selected`}
          search={headSearch}
          onSearch={setHeadSearch}
          searchPlaceholder="Search heads..."
          accent="emerald"
          allChecked={selectedHeadIds.length === committeeHeads.length && committeeHeads.length > 0}
          onToggleAll={(checked) => {
            setSelectedHeadIds(checked ? committeeHeads.map((head) => head.id) : []);
            setSelectedAssigneeIds([]);
          }}
          emptyText="No heads found"
          options={searchedHeads.map((head) => ({
            id: head.id,
            title: head.name,
            subtitle: (head.managedDepartments || []).join(' | '),
            checked: selectedHeadIds.includes(head.id),
            onChange: (checked: boolean) => {
              setSelectedHeadIds((current) => checked ? [...current, head.id] : current.filter((id) => id !== head.id));
              setSelectedAssigneeIds([]);
            },
          }))}
        />
        <FilterMenu
          menuRef={memberRef}
          open={openMenu === 'member'}
          onToggle={() => setOpenMenu(openMenu === 'member' ? null : 'member')}
          icon={<Users className="w-3 h-3 text-indigo-500" />}
          label="Committee Member"
          value={selectedAssigneeIds.length === 0 ? 'All Members' : selectedAssigneeIds.length === 1 ? (filteredMembers.find((member) => member.id === selectedAssigneeIds[0])?.name || 'Selected') : `${selectedAssigneeIds.length} Selected`}
          search={memberSearch}
          onSearch={setMemberSearch}
          searchPlaceholder="Search members..."
          accent="indigo"
          allChecked={selectedAssigneeIds.length === filteredMembers.length && filteredMembers.length > 0}
          onToggleAll={(checked) => setSelectedAssigneeIds(checked ? filteredMembers.map((member) => member.id) : [])}
          emptyText="No members found"
          options={searchedMembers.map((member) => ({
            id: member.id,
            title: member.name,
            subtitle: (member.managedDepartments || []).join(' | '),
            checked: selectedAssigneeIds.includes(member.id),
            onChange: (checked: boolean) => {
              setSelectedAssigneeIds((current) => checked ? [...current, member.id] : current.filter((id) => id !== member.id));
            },
          }))}
        />
      </div>

      {hasTopFilters && (
        <div className="mb-6 flex flex-col gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Active Filters:</span>
          <div className="flex flex-wrap items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200/80 shadow-sm">
            {selectedCommitteeNames.map((name) => (
              <FilterChip key={name} tone="purple" prefix="Committee" label={name} onRemove={() => setSelectedCommitteeNames((current) => current.filter((item) => item !== name))} />
            ))}
            {selectedLeaderIds.map((id) => {
              const leader = allLeaders.find((item) => item.id === id);
              if (!leader) return null;
              return <FilterChip key={id} tone="blue" prefix="Leader" label={leader.name} detail={leader.managedDepartments?.join(' | ')} onRemove={() => setSelectedLeaderIds((current) => current.filter((item) => item !== id))} />;
            })}
            {selectedHeadIds.map((id) => {
              const head = committeeHeads.find((item) => item.id === id);
              if (!head) return null;
              return <FilterChip key={id} tone="emerald" prefix="Head" label={head.name} detail={head.managedDepartments?.join(' | ')} onRemove={() => setSelectedHeadIds((current) => current.filter((item) => item !== id))} />;
            })}
            {selectedAssigneeIds.map((id) => {
              const member = filteredMembers.find((item) => item.id === id);
              if (!member) return null;
              return <FilterChip key={id} tone="indigo" prefix="Member" label={member.name} detail={member.managedDepartments?.join(' | ')} onRemove={() => setSelectedAssigneeIds((current) => current.filter((item) => item !== id))} />;
            })}
            <button
              type="button"
              onClick={() => {
                setSelectedCommitteeNames([]);
                setSelectedLeaderIds([]);
                setSelectedHeadIds([]);
                setSelectedAssigneeIds([]);
              }}
              className="text-xs font-medium text-slate-500 hover:text-slate-800 bg-white border border-slate-200 hover:border-slate-300 px-2.5 py-1.5 rounded-lg shadow-sm transition-all ml-1"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {statsLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
          {cards.map((card) => (
            <div key={card.label} className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className={`${card.tone} p-1.5 rounded-lg shrink-0`}>{card.icon}</div>
                <p className="text-xs text-slate-600 font-semibold truncate" title={card.label}>{card.label}</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{card.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8">
        <IdeasFilterTable
          apiEndpoint="/admin/mis/ideas"
          queryKeyPrefix="misIdeas"
          showExport
          actionButtonLabel="Evaluate"
          evaluatePath={(id) => `/mis/ideas/${id}`}
          includeMisStatuses
          misQueueOnly
          misExport
          onMisEdit={setDecisionIdeaId}
          groupsEndpoint="/admin/mis/department-groups"
          mappingsEndpoint="/admin/mis/user-role-mappings"
          assigneeId={finalAssigneeIds.length > 0 ? finalAssigneeIds.join(',') : undefined}
          baseDepartment={effectiveDepartmentIds}
        />
      </div>
      {decisionIdeaId && <MisDecisionModal ideaId={decisionIdeaId} onClose={() => setDecisionIdeaId(null)} />}
    </Layout>
  );
}

function FilterChip({ tone, prefix, label, detail, onRemove }: { tone: string; prefix: string; label: string; detail?: string; onRemove: () => void }) {
  const tones: Record<string, string> = {
    purple: 'bg-purple-100 text-purple-800 border-purple-200/50',
    blue: 'bg-blue-100 text-blue-800 border-blue-200/50',
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200/50',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200/50',
  };
  return (
    <div className={`${tones[tone]} text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm border`}>
      <span className="font-medium opacity-75 mr-0.5">{prefix}:</span>
      <span className="font-semibold">{label}</span>
      {detail && <span className="border-l border-current/30 pl-1.5 font-medium">{detail}</span>}
      <button type="button" onClick={onRemove} className="ml-0.5 opacity-70 hover:opacity-100">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function FilterMenu({
  menuRef, open, onToggle, icon, label, value, search, onSearch, searchPlaceholder, accent, allChecked, onToggleAll, options, emptyText,
}: {
  menuRef: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  onToggle: () => void;
  icon: React.ReactNode;
  label: string;
  value: string;
  search: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
  accent: 'purple' | 'blue' | 'emerald' | 'indigo';
  allChecked: boolean;
  onToggleAll: (checked: boolean) => void;
  options: { id: string; title: string; subtitle?: string; checked: boolean; onChange: (checked: boolean) => void }[];
  emptyText: string;
}) {
  const checkbox = {
    purple: 'text-purple-600 focus:ring-purple-500',
    blue: 'text-blue-600 focus:ring-blue-500',
    emerald: 'text-emerald-600 focus:ring-emerald-500',
    indigo: 'text-indigo-600 focus:ring-indigo-500',
  }[accent];
  const hover = {
    purple: 'group-hover:text-purple-700',
    blue: 'group-hover:text-blue-700',
    emerald: 'group-hover:text-emerald-700',
    indigo: 'group-hover:text-indigo-700',
  }[accent];
  const ring = {
    purple: 'focus:border-purple-400 focus:ring-purple-400',
    blue: 'focus:border-blue-400 focus:ring-blue-400',
    emerald: 'focus:border-emerald-400 focus:ring-emerald-400',
    indigo: 'focus:border-indigo-400 focus:ring-indigo-400',
  }[accent];

  return (
    <div className="flex flex-col flex-1 min-w-[150px] max-w-[240px]" ref={menuRef}>
      <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
        {icon}
        {label}
      </label>
      <div className={`relative ${open ? 'z-50' : 'z-20'}`}>
        <Filter className="w-4 h-4 absolute left-2.5 top-1.5 text-slate-400 pointer-events-none" />
        <button
          type="button"
          onClick={onToggle}
          className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 transition-colors text-left flex justify-between items-center shadow-sm"
        >
          <span className="truncate font-medium text-slate-700">{value}</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1.5 w-full sm:w-[280px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
            <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(event) => onSearch(event.target.value)}
                  className={`w-full text-xs pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 ${ring}`}
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-60 flex-1">
              <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 bg-slate-50/50">
                <input
                  type="checkbox"
                  className={`mt-0.5 mr-3 cursor-pointer rounded border-slate-300 ${checkbox}`}
                  checked={allChecked}
                  onChange={(event) => onToggleAll(event.target.checked)}
                />
                <span className="text-sm text-slate-800 font-bold">Select All</span>
              </label>
              {options.length === 0 ? (
                <div className="px-4 py-4 text-sm text-slate-500 text-center font-medium">{emptyText}</div>
              ) : options.map((option) => (
                <label key={option.id} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                  <input
                    type="checkbox"
                    className={`mt-0.5 mr-3 cursor-pointer rounded border-slate-300 shrink-0 ${checkbox}`}
                    checked={option.checked}
                    onChange={(event) => option.onChange(event.target.checked)}
                  />
                  <div className="flex flex-col overflow-hidden">
                    <span className={`truncate leading-tight text-slate-700 font-medium transition-colors ${hover}`}>{option.title}</span>
                    {option.subtitle && <span className="text-slate-500 text-[10px] uppercase font-bold leading-tight truncate mt-0.5">{option.subtitle}</span>}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
